import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CountryModel,
  SpecialCountryModel,
} from '../models/customer-maintenance/country.model';
import { ApiService } from './api.service';
import { EquipmentItemModel } from '../models/master-list/equipment-item.model';
import {
  LynncoServiceModel,
  LynncoServiceNewModel,
} from '../models/master-list/lynnco-service.model';
import {
  TransportationModeModel,
  TransportationModeNewModel,
} from '../models/master-list/transportation-mode.model';
import { CustomerPaymentTermModel } from '../models/master-list/customer-payment-term.model';
import { CurrencyModel } from '../models/master-list/currency.model';
import { BillingPeriodModel } from '../models/master-list/billing-period.model';
import { InvoiceTemplateModel } from '../models/master-list/invoice-template.model';
import { CarrierPaymentTermModel } from '../models/master-list/carrier-payment-term.model';
import { CustomerStatusModel } from '../models/master-list/customer-status.model';
import { DirectionModel } from '../models/master-list/direction.model';
import { BusinessUnitModel } from '../models/master-list/business-unit.model';
import { ContinentModel } from '../models/master-list/continent.model';
import { DocumentTypeModel } from '../models/lynnco-admin/document-type.model';
import { LocationModel } from '../models/customer-maintenance/location.model';
import { CustomerModel } from '../models/small-pack/customer.model';
import { MasterListChargeCode } from '../models/master-list/charge-code.model';
import { SystemProcessModel } from '../models/master-list/system-process.model';
import {
  CarrierShipmentExceptionsModel,
  CustomerShipmentExceptionsModel,
  CustomerWithoutExpenseCodeModel,
} from '../models/master-list/customer-shipment-exceptions.model';
import { CustomerCarrierDropdownModel } from '../models/small-pack/manual-invoice-search/customer-carrier-dropdown.model';
import { map } from 'rxjs/operators';
import { GeneralResponseModel } from '../models/general-response.model';
import { CustomerSpecialLocationModel } from '../models/customer-maintenance/customer.model';
import { ExpenseCodeMappingOperatorModel } from '../models/customer-maintenance/invoice/expense-code-mapping.model';
import { CustomerExpenseCodeModel } from '../models/customer-maintenance/invoice/expense-code.model';
import { CustomerCarrierAndLocationsModel } from '../models/small-pack/customer-carrier-account.model';
import { ExpenseCodeMappingReferenceModel } from '../models/master-list/expense-code-mapping-reference.model';
import { ReferenceTypeNewModel } from '../models/customer-maintenance/sys-config/reference-type.model';

@Injectable({
  providedIn: 'root',
})
export class MasterListService {
  constructor(private readonly apiService: ApiService) {}

  getAddressTypes(): Observable<any> {
    return this.apiService.get<any>(environment.hairball.addressTypes);
  }

  getStates(): Observable<CountryModel[]> {
    return this.apiService.get<CountryModel[]>(environment.hairball.states);
  }

  getEquipment(): Observable<EquipmentItemModel[]> {
    const url = environment.masterList.Equipment;
    return this.apiService.get<EquipmentItemModel[]>(url);
  }

  getLynncoServices(): Observable<LynncoServiceModel[]> {
    const url = environment.masterList.LynnCoServices;
    return this.apiService.get<LynncoServiceModel[]>(url);
  }

  getTransportationModes(): Observable<TransportationModeModel[]> {
    const url = environment.masterList.TransportationMode;
    return this.apiService.get<TransportationModeModel[]>(url);
  }

  getAllLynncoServices(): Observable<LynncoServiceNewModel[]> {
    const url = environment.masterList.lynncoServices;
    return this.apiService
      .get<LynncoServiceNewModel[]>(url)
      .pipe(map((response: any) => response.data));
  }

  getAllTransportationModes(): Observable<TransportationModeNewModel[]> {
    const url = environment.masterList.transportationModes;
    return this.apiService
      .get<TransportationModeNewModel[]>(url)
      .pipe(map((response: any) => response.data));
  }

  getExpenseCodeMappingReferences(): Observable<
    ExpenseCodeMappingReferenceModel[]
  > {
    return this.apiService
      .get<
        ExpenseCodeMappingReferenceModel[]
      >(environment.masterList.expenseCodeMappingReferences)
      .pipe(map((response: any) => response.data));
  }

  getCustomerPaymentTerms(): Observable<CustomerPaymentTermModel[]> {
    const url = environment.masterList.CustomerPaymentTerms;
    return this.apiService.get<CustomerPaymentTermModel[]>(url);
  }

  getCarrierPaymentTerms(): Observable<CarrierPaymentTermModel[]> {
    const url = environment.masterList.CarrierPaymentTerms;
    return this.apiService.get<CarrierPaymentTermModel[]>(url);
  }

  getCurrency(): Observable<CurrencyModel[]> {
    const url = environment.masterList.Currency;
    return this.apiService.get<CurrencyModel[]>(url);
  }

  getBillingPeriod(): Observable<BillingPeriodModel[]> {
    return this.apiService.get<BillingPeriodModel[]>(
      environment.masterList.billingPeriod,
    );
  }

  getInvoiceTemplates(): Observable<InvoiceTemplateModel[]> {
    return this.apiService.get<InvoiceTemplateModel[]>(
      environment.masterList.invoiceTemplate,
    );
  }
 
  getCustomerStatusList(): Observable<CustomerStatusModel[]> {
    return this.apiService.get<CustomerStatusModel[]>(
      environment.masterList.customerStatus,
    );
  }

  getDirections(): Observable<DirectionModel[]> {
    const url = environment.masterList.Directions;
    return this.apiService.get<DirectionModel[]>(url);
  }

  getBusinessUnit(): Observable<BusinessUnitModel[]> {
    return this.apiService.get<BusinessUnitModel[]>(
      environment.masterList.businessUnit,
    );
  }

  getContinents(): Observable<ContinentModel[]> {
    const url = environment.masterList.Continent;
    return this.apiService.get<ContinentModel[]>(url);
  }

  getDocumentTypes(): Observable<DocumentTypeModel[]> {
    const url = environment.masterList.DocType;
    return this.apiService.get<DocumentTypeModel[]>(url);
  }

  getCustomerLocations(customerID: number): Observable<LocationModel[]> {
    return this.apiService
      .get<
        LocationModel[]
      >(environment.masterList.customerLocationList + customerID)
      .pipe(map((response: any) => response?.Data || []));
  }

  getAllCustomers(): Observable<CustomerModel[]> {
    const url = environment.masterList.Customers;
    return this.apiService.get<CustomerModel[]>(url);
  }

  getChargeCodes(): Observable<MasterListChargeCode[]> {
    const url = environment.masterList.ChargeCodes;
    return this.apiService.get<MasterListChargeCode[]>(url);
  }

  getSystemProcess(): Observable<SystemProcessModel[]> {
    const url = environment.masterList.systemProcess;
    return this.apiService.get<SystemProcessModel[]>(url);
  }

  getCustomerWithShipmentExceptions(customerTypeId: number = 0): Observable<CustomerShipmentExceptionsModel> {
    const url = environment.masterList.customerWithShipmentExceptions;
    return this.apiService.get<CustomerShipmentExceptionsModel>(url + (customerTypeId !== 0 ? '/' + customerTypeId : ''));
  }
  
  getCarriersWithShipmentExceptions(customerTypeId: number = 0): Observable<CarrierShipmentExceptionsModel> {
    const url = environment.masterList.carriersWithShipmentExceptions;
    return this.apiService.get<CarrierShipmentExceptionsModel>(url + (customerTypeId !== 0 ? '/' + customerTypeId : ''));
  }


  getCustomerCarrierAccounts(): Observable<CustomerCarrierDropdownModel[]> {
    return this.apiService
      .get<
        CustomerCarrierDropdownModel[]
      >(environment.masterList.customerCarrierAccounts)
      .pipe(map((response: any) => response.data));
  }

  getAllCountries(): Observable<SpecialCountryModel[]> {
    const url = environment.masterList.Country;
    return this.apiService.get<SpecialCountryModel[]>(url);
  }

  getCustomerSpecialLocations(
    customerID: number,
    active: number,
  ): Observable<GeneralResponseModel<CustomerSpecialLocationModel[]>> {
    return this.apiService.get<
      GeneralResponseModel<CustomerSpecialLocationModel[]>
    >(environment.masterList.customerSpecialLocations + customerID, {
      active: active,
    });
  }

  getexpenseCodeMappingOperators(): Observable<
    GeneralResponseModel<ExpenseCodeMappingOperatorModel[]>
  > {
    return this.apiService.get<
      GeneralResponseModel<ExpenseCodeMappingOperatorModel[]>
    >(environment.masterList.expenseCodeMappingOperators);
  }

  getExpenseCodeByCustomer(
    customerId: number,
    active = false,
  ): Observable<CustomerExpenseCodeModel[]> {
    return this.apiService
      .get<CustomerExpenseCodeModel[]>(
        environment.hairball.expenseCodeByCustomerId + customerId,
        {
          active: active,
        },
      )
      .pipe(map((response: any) => response.data));
  }

  getCustomersWithoutExpenseCodes(): Observable<
    GeneralResponseModel<CustomerWithoutExpenseCodeModel[]>
  > {
    return this.apiService.get<
      GeneralResponseModel<CustomerWithoutExpenseCodeModel[]>
    >(environment.masterList.customersWithShipmentWithoutExpCode);
  }

  getCustomersCarriersAndLocations(): Observable<
    GeneralResponseModel<CustomerCarrierAndLocationsModel>
  > {
    return this.apiService.get<
      GeneralResponseModel<CustomerCarrierAndLocationsModel>
    >(environment.masterList.smallPackCarriersAndCustomers);
  }

  getAllReferenceTypes(): Observable<ReferenceTypeNewModel[]> {
    return this.apiService
      .get<ReferenceTypeNewModel[]>(environment.masterList.referenceTypes)
      .pipe(map((response: any) => response?.data));
  }
}
